/**
 * CUSTOM: StaggeredMenu — vanilla JS port of ReactBits StaggeredMenu component.
 * Fullscreen slide-out navigation with staggered item animations.
 * Uses Web Animations API (GPU-composited), no GSAP dependency.
 *
 * Hijacks the Horizon header-drawer hamburger so clicking it opens this menu
 * instead of the native drawer. No Horizon core files are modified.
 */
(function () {
  'use strict';

  var WRAPPER_SEL = '[data-staggered-menu]';

  function initMenu(wrapper) {
    var position = wrapper.dataset.position || 'right';
    var panel = wrapper.querySelector('.sm-panel');
    var prelayers = Array.from(wrapper.querySelectorAll('.sm-prelayer'));
    var toggleBtn = wrapper.querySelector('.sm-toggle');
    var icon = wrapper.querySelector('.sm-icon');
    var textInner = wrapper.querySelector('.sm-toggle-textInner');
    var overlay = wrapper.querySelector('.sm-overlay');
    var closeBtn = wrapper.querySelector('.sm-close');

    if (!panel || !toggleBtn) return;

    var isOpen = false;
    var busy = false;
    var currentAnimations = [];

    var offscreen = position === 'left' ? '-100%' : '100%';

    /* ── Set initial states ──
       Wrapper renders with inline `visibility: hidden` so nothing inside paints
       before JS runs. Lock in the off-screen transforms first, *then* reveal
       the wrapper — guarantees no flash of the panel on first load. */
    panel.style.transform = 'translateX(' + offscreen + ')';
    prelayers.forEach(function (l) {
      l.style.transform = 'translateX(' + offscreen + ')';
    });
    wrapper.style.visibility = '';

    /* ── Animation helpers ── */
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function animate(el, keyframes, options) {
      /* Reduced motion: jump straight to the final state, no stagger */
      if (reduceMotion) {
        options = Object.assign({}, options, { duration: 0, delay: 0 });
      }
      var anim = el.animate(keyframes, options);
      currentAnimations.push(anim);
      return anim;
    }

    function cancelAll() {
      currentAnimations.forEach(function (a) {
        try { a.cancel(); } catch (e) {}
      });
      currentAnimations = [];
    }

    function commitTransform(el, value) {
      el.style.transform = value;
    }

    /* ── Open ── */
    function openMenu() {
      if (busy || isOpen) return;
      busy = true;
      isOpen = true;
      wrapper.setAttribute('data-open', '');
      panel.setAttribute('aria-hidden', 'false');
      panel.removeAttribute('inert');
      document.body.style.overflow = 'hidden';

      cancelAll();

      /* Overlay fade in */
      if (overlay) {
        overlay.style.display = 'block';
        animate(overlay, [
          { opacity: 0 },
          { opacity: 1 }
        ], { duration: 400, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'forwards' });
      }

      /* Pre-layers slide in with stagger */
      prelayers.forEach(function (layer, i) {
        var a = animate(layer, [
          { transform: 'translateX(' + offscreen + ')' },
          { transform: 'translateX(0)' }
        ], {
          duration: 500,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          delay: i * 70,
          fill: 'forwards'
        });
        a.onfinish = function () { commitTransform(layer, 'translateX(0)'); };
      });

      /* Panel slides in */
      var panelDelay = prelayers.length * 70 + 80;
      var panelAnim = animate(panel, [
        { transform: 'translateX(' + offscreen + ')' },
        { transform: 'translateX(0)' }
      ], {
        duration: 650,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        delay: panelDelay,
        fill: 'forwards'
      });
      panelAnim.onfinish = function () { commitTransform(panel, 'translateX(0)'); };

      /* Menu items stagger in */
      var items = Array.from(panel.querySelectorAll('.sm-panel-itemLabel'));
      items.forEach(function (item, i) {
        item.style.transform = 'translateY(140%) rotate(10deg)';
        item.style.opacity = '0';
        var a = animate(item, [
          { transform: 'translateY(140%) rotate(10deg)', opacity: 0 },
          { transform: 'translateY(0) rotate(0deg)', opacity: 1 }
        ], {
          duration: 800,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          delay: panelDelay + 100 + i * 100,
          fill: 'forwards'
        });
        a.onfinish = function () {
          item.style.transform = 'translateY(0) rotate(0deg)';
          item.style.opacity = '1';
        };
      });

      /* Number counters fade in */
      var numbers = Array.from(panel.querySelectorAll('.sm-panel-item'));
      numbers.forEach(function (num, i) {
        num.style.setProperty('--sm-num-opacity', '0');
        var a = animate(num, [
          { '--sm-num-opacity': 0 },
          { '--sm-num-opacity': 1 }
        ], {
          duration: 500,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
          delay: panelDelay + 200 + i * 80,
          fill: 'forwards'
        });
        /* Fallback: set via timeout since custom properties may not animate in all browsers */
        setTimeout(function () {
          num.style.setProperty('--sm-num-opacity', '1');
        }, panelDelay + 200 + i * 80 + 500);
      });

      /* Social links fade/slide in */
      var socialTitle = panel.querySelector('.sm-socials-title');
      var socialLinks = Array.from(panel.querySelectorAll('.sm-socials-link'));
      var socialDelay = panelDelay + 260;

      if (socialTitle) {
        socialTitle.style.opacity = '0';
        var stA = animate(socialTitle, [
          { opacity: 0 },
          { opacity: 1 }
        ], { duration: 500, easing: 'ease-out', delay: socialDelay, fill: 'forwards' });
        stA.onfinish = function () { socialTitle.style.opacity = '1'; };
      }

      socialLinks.forEach(function (link, i) {
        link.style.transform = 'translateY(25px)';
        link.style.opacity = '0';
        var a = animate(link, [
          { transform: 'translateY(25px)', opacity: 0 },
          { transform: 'translateY(0)', opacity: 1 }
        ], {
          duration: 550,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          delay: socialDelay + 40 + i * 80,
          fill: 'forwards'
        });
        a.onfinish = function () {
          link.style.transform = 'translateY(0)';
          link.style.opacity = '1';
        };
      });

      /* Icon: plus → X (rotate 225deg) — hidden toggle, but keep for consistency */
      animate(icon, [
        { transform: 'rotate(0deg)' },
        { transform: 'rotate(225deg)' }
      ], { duration: 600, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' });

      /* Text cycle: Menu → Close */
      if (textInner) {
        textInner.style.transform = 'translateY(0)';
        animate(textInner, [
          { transform: 'translateY(0)' },
          { transform: 'translateY(-50%)' }
        ], { duration: 500, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' });
      }

      /* Toggle button color */
      toggleBtn.classList.add('sm-toggle--open');

      /* Swap header hamburger icon to X */
      setHamburgerState(true);

      var totalDur = panelDelay + 800 + items.length * 100;
      setTimeout(function () { busy = false; }, totalDur);
    }

    /* ── Close ── */
    function closeMenu() {
      if (busy || !isOpen) return;
      busy = true;
      isOpen = false;
      wrapper.removeAttribute('data-open');
      panel.setAttribute('aria-hidden', 'true');
      panel.setAttribute('inert', '');

      cancelAll();

      /* Overlay fade out */
      if (overlay) {
        var oa = animate(overlay, [
          { opacity: 1 },
          { opacity: 0 }
        ], { duration: 300, easing: 'ease-in', fill: 'forwards' });
        oa.onfinish = function () { overlay.style.display = 'none'; };
      }

      /* Slide everything out */
      var all = prelayers.concat([panel]);
      all.forEach(function (el) {
        var a = animate(el, [
          { transform: 'translateX(0)' },
          { transform: 'translateX(' + offscreen + ')' }
        ], { duration: 320, easing: 'cubic-bezier(0.55, 0, 1, 0.45)', fill: 'forwards' });
        a.onfinish = function () { commitTransform(el, 'translateX(' + offscreen + ')'); };
      });

      /* Reset item labels */
      var items = Array.from(panel.querySelectorAll('.sm-panel-itemLabel'));
      items.forEach(function (item) {
        item.style.transform = 'translateY(140%) rotate(10deg)';
        item.style.opacity = '0';
      });

      /* Reset numbers */
      var numbers = Array.from(panel.querySelectorAll('.sm-panel-item'));
      numbers.forEach(function (num) {
        num.style.setProperty('--sm-num-opacity', '0');
      });

      /* Reset social links */
      var socialTitle = panel.querySelector('.sm-socials-title');
      var socialLinks = Array.from(panel.querySelectorAll('.sm-socials-link'));
      if (socialTitle) socialTitle.style.opacity = '0';
      socialLinks.forEach(function (link) {
        link.style.transform = 'translateY(25px)';
        link.style.opacity = '0';
      });

      /* Icon: X → plus (rotate back) */
      animate(icon, [
        { transform: 'rotate(225deg)' },
        { transform: 'rotate(0deg)' }
      ], { duration: 350, easing: 'cubic-bezier(0.33, 0, 0.67, 1)', fill: 'forwards' });

      /* Text cycle: Close → Menu */
      if (textInner) {
        animate(textInner, [
          { transform: 'translateY(-50%)' },
          { transform: 'translateY(0)' }
        ], { duration: 400, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' });
      }

      toggleBtn.classList.remove('sm-toggle--open');
      document.body.style.overflow = '';

      /* Reset header hamburger icon back to hamburger */
      setHamburgerState(false);

      setTimeout(function () { busy = false; }, 350);
    }

    /* ── Toggle ── */
    function toggle() {
      if (isOpen) closeMenu();
      else openMenu();
    }

    toggleBtn.addEventListener('click', toggle);

    /* Close on the in-panel X button */
    if (closeBtn) closeBtn.addEventListener('click', closeMenu);

    /* Close on overlay click */
    if (overlay) {
      overlay.addEventListener('click', function () {
        if (isOpen) closeMenu();
      });
    }

    /* Close on Escape key */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) closeMenu();
    });

    /* Hide the floating toggle button — header hamburger is used instead */
    toggleBtn.style.display = 'none';

    /* Return toggle so we can wire it to the header hamburger */
    return toggle;
  }

  /* ── Swap hamburger ↔ X icon in header ── */
  function setHamburgerState(menuIsOpen) {
    var details = document.querySelector('#Details-menu-drawer-container');
    if (!details) return;

    /* Horizon shows/hides icons based on .menu-open class on the <details> element */
    if (menuIsOpen) {
      details.classList.add('menu-open');
    } else {
      details.classList.remove('menu-open');
    }

    var summary = details.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', menuIsOpen ? 'true' : 'false');
    }
  }

  /* ── Override Horizon's HeaderDrawer component ── */
  function hijackHeaderDrawer(toggleFn) {
    /*
     * Horizon's <header-drawer> custom element has toggle/open/close methods
     * called via on:click="/toggle". We override these methods directly on the
     * element instance so the hamburger icon opens our staggered menu instead
     * of the native drawer.
     */
    var headerDrawer = document.querySelector('header-drawer');
    if (headerDrawer) {
      patchElement(headerDrawer, toggleFn);
      return;
    }

    /* If the element isn't in the DOM yet, wait for it */
    if (typeof customElements !== 'undefined' && customElements.whenDefined) {
      customElements.whenDefined('header-drawer').then(function () {
        var el = document.querySelector('header-drawer');
        if (el) patchElement(el, toggleFn);
      });
    }
  }

  function patchElement(el, toggleFn) {
    /* Override toggle so hamburger click opens staggered menu */
    el.toggle = function () {
      toggleFn();
    };

    /* Override open to prevent native drawer from showing */
    el.open = function () {
      toggleFn();
    };

    /* Override close to be a no-op (staggered menu handles its own close) */
    el.close = function () {};

    /*
     * Prevent the native <details> toggle from firing.
     * Clicking <summary> natively opens <details>, which would show
     * the Horizon drawer alongside the staggered menu.
     * We intercept the click on the summary, prevent default,
     * and call our toggle instead.
     */
    var summary = el.querySelector('summary.header__icon--summary');
    if (summary) {
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        toggleFn();
      });
    }
  }

  /* ── Init ── */
  function init() {
    var wrappers = document.querySelectorAll(WRAPPER_SEL);
    wrappers.forEach(function (wrapper) {
      var toggleFn = initMenu(wrapper);
      if (toggleFn) {
        hijackHeaderDrawer(toggleFn);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
